// Lead management API. Unlike most admin endpoints these require the login
// token — leads are personal data — so every call sends it.
const API_URL = process.env.REACT_APP_API_URL;

async function request(method, path, body, { isForm = false } = {}) {
  const token = localStorage.getItem("accessToken");
  const res = await fetch(`${API_URL}/api${path}`, {
    method,
    headers: {
      ...(isForm ? {} : { "Content-Type": "application/json" }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    const err = new Error(json.message || `Request failed (${res.status})`);
    err.status = res.status;
    err.errors = json.errors;
    throw err;
  }
  return json;
}

const qs = (params = {}) => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length)) return;
    p.set(k, Array.isArray(v) ? v.join(",") : v);
  });
  const s = p.toString();
  return s ? `?${s}` : "";
};

export const leadApi = {
  meta: () => request("GET", "/leads/meta").then((r) => r.data),
  dashboard: (params) => request("GET", `/leads/dashboard${qs(params)}`).then((r) => r.data),
  list: (params) => request("GET", `/leads${qs(params)}`),
  // Every sent / failed message, latest first → { data, total, page, limit }
  notificationLog: (params) => request("GET", `/leads/notification-log${qs(params)}`),
  // Queue every failed message (the log's filters apply) again; dryRun: only count → { resend | queued, failedToSend, notDelivered, payment, leadDeleted, duplicates }
  resendFailed: (filters, dryRun = false) => request("POST", "/leads/notification-log/resend-failed", { filters, dryRun }).then((r) => r.data),
  // Ask UraanWapp for the delivery of sent messages not checked since `since` → { checked, changed, remaining, rateLimited }
  refreshLog: (filters, since) => request("POST", "/leads/notification-log/refresh", { filters, since }).then((r) => r.data),
  refreshLeadMessages: (id, since) => request("POST", `/leads/${id}/messages/refresh`, { since }).then((r) => r.data),
  get: (id) => request("GET", `/leads/${id}`).then((r) => r.data),
  // { sent, upcoming, notSent } — the lead's notifications.
  messages: (id) => request("GET", `/leads/${id}/messages`).then((r) => r.data),
  // The leads before / after this one in the list it was opened from.
  // listQuery: that list's filters, as its address bar had them ("statuses=…&sort=…").
  // → { prev, next, position, total }
  neighbors: (id, listQuery = "") => request("GET", `/leads/${id}/neighbors${listQuery ? `?${listQuery}` : ""}`).then((r) => r.data),
  // Many leads at once: { <leadId>: { sent, upcoming, notSent } }.
  messagesBatch: (ids) => request("POST", "/leads/messages-batch", { ids }).then((r) => r.data),
  create: (body) => request("POST", "/leads", body),
  update: (id, body) => request("PUT", `/leads/${id}`, body).then((r) => r.data),
  remove: (id) => request("DELETE", `/leads/${id}`),
  // After a call / WhatsApp: { status?, channel: "call"|"whatsapp", summary?, followUpAt?, clearFollowUp?, demoSessionId? }
  logUpdate: (id, body) => request("POST", `/leads/${id}/update`, body).then((r) => r.data),
  setAutomation: (id, on) => request("POST", `/leads/${id}/automation`, { on }).then((r) => r.data),
  sendCustom: (id, customMessageId, channels = ["whatsapp"]) => request("POST", `/leads/${id}/custom-message`, { customMessageId, channels }).then((r) => r.data),
  // ── Bulk (the lead list) ──
  // Every lead the list's filters match (≤ 5,000): [{ _id, fullName, courseId, courseTitle, status }]
  ids: (params) => request("GET", `/leads/ids${qs(params)}`).then((r) => r.data),
  // ≤ 50 ids per call: { ids, status, summary, followUpAt?, demoSessions?: { courseId: sessionId } } → [{ _id, fullName, result, reason }]
  bulkUpdate: (body) => request("POST", "/leads/bulk-update", body).then((r) => r.data),
  bulkMessageOptions: (ids) => request("POST", "/leads/bulk-message/options", { ids }).then((r) => r.data),
  // { ids, title, channels } → { queued, results }
  bulkMessage: (body) => request("POST", "/leads/bulk-message", body).then((r) => r.data),
  sendPaymentLink: (id, body) => request("POST", `/leads/${id}/payment-link`, body).then((r) => r.data),
  resendMessage: (messageId) => request("POST", `/leads/messages/${messageId}/resend`).then((r) => r.data),
  runAutomation: () => request("POST", "/leads/automation/run").then((r) => r.data),
  filterOptions: () => request("GET", "/leads/filter-options").then((r) => r.data),
  metaSync: () => request("GET", "/leads/meta-sync").then((r) => r.data),
  runMetaSync: (body) => request("POST", "/leads/meta-sync/run", body).then((r) => r.data),
  checkMetaConnection: () => request("GET", "/leads/meta-sync/check").then((r) => r.data),
};

export const leadSetupApi = {
  campaigns: (params) => request("GET", `/lead-setup/campaigns${qs(params)}`).then((r) => r.data),
  createCampaign: (body) => request("POST", "/lead-setup/campaigns", body).then((r) => r.data),
  updateCampaign: (id, body) => request("PUT", `/lead-setup/campaigns/${id}`, body).then((r) => r.data),
  deleteCampaign: (id) => request("DELETE", `/lead-setup/campaigns/${id}`),
  automation: (courseId) => request("GET", `/lead-setup/automation/${courseId}`).then((r) => r.data),
  saveAutomation: (courseId, body) => request("PUT", `/lead-setup/automation/${courseId}`, body).then((r) => r.data),
  demoSessions: (params) => request("GET", `/lead-setup/demo-sessions${qs(params)}`).then((r) => r.data),
  createDemoSession: (body) => request("POST", "/lead-setup/demo-sessions", body).then((r) => r.data),
  updateDemoSession: (id, body) => request("PUT", `/lead-setup/demo-sessions/${id}`, body),
  deleteDemoSession: (id) => request("DELETE", `/lead-setup/demo-sessions/${id}`),
  // The templates on the UraanWapp account: { configured, error?, data: [{ id, name, status, sendable, mediaType, params, bodyPreview, subject }] }
  providerTemplates: (channel, { fresh = false } = {}) => request("GET", `/lead-setup/provider-templates?channel=${channel}${fresh ? "&fresh=1" : ""}`),
  uploadAttachment: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return request("POST", "/lead-setup/attachments", fd, { isForm: true });
  },
};

// Not lead-specific, but needed by the settings screens.
export const fetchCoursesForLeads = async () => {
  const res = await fetch(`${API_URL}/api/public/lead-courses`);
  const json = await res.json();
  return json.data || [];
};
